import { EARLY_JOIN_MS, fmtMoment } from "./meetingFormat";

interface HttpLike {
  status: number;
  code: string;
  message: string;
}

/** ApiError (signed in) and PublicError (guest) both carry status / code / message. */
export const asHttp = (e: unknown): HttpLike => {
  const o = (e ?? {}) as Partial<HttpLike>;
  return { status: typeof o.status === "number" ? o.status : -1, code: typeof o.code === "string" ? o.code : "", message: typeof o.message === "string" ? o.message : "" };
};

export type JoinFailKind = "passcode" | "notopen" | "retry" | "fatal";
export interface JoinFail {
  kind: JoinFailKind;
  text: string;
  /** for "notopen": when the room opens for non-hosts, when known */
  opensAtMs?: number;
}

/** Every join error the contract lists, as a message a person can act on. `ctx.startMs` lets "not opened yet" say when. */
export function describeJoinError(e: unknown, ctx: { startMs?: number | undefined; now?: number } = {}): JoinFail {
  const { status, message } = asHttp(e);
  const m = message.toLowerCase();
  const now = ctx.now ?? Date.now();
  switch (status) {
    case 0:
      return { kind: "retry", text: "Can't reach the server. Check your connection and try again." };
    case 401:
      return { kind: "fatal", text: "Your session has ended. Sign in again to join this meeting." };
    case 400:
      return { kind: "fatal", text: message || "That meeting link isn't valid." };
    case 403:
      if (m.includes("removed")) return { kind: "fatal", text: "The host removed you from this meeting, so you can't join it again." };
      if (m.includes("did not admit") || m.includes("denied")) return { kind: "fatal", text: "The host didn't let you in." };
      if (m.includes("incorrect")) return { kind: "passcode", text: "That passcode isn't right. Check it and try again." };
      if (m.includes("passcode")) return { kind: "passcode", text: "This meeting needs a passcode. Enter it to join." };
      if (m.includes("register")) return { kind: "fatal", text: "You need to register for this webinar before you can join." };
      if (m.includes("guest")) return { kind: "fatal", text: "Guests can't join meetings in this organisation. Sign in to join." };
      return { kind: "fatal", text: message ? `You can't join this meeting: ${message}` : "You don't have permission to join this meeting." };
    case 404:
      return { kind: "fatal", text: "We couldn't find this meeting. The link may be wrong, the meeting may have been cancelled, or you may not have access to it." };
    case 409:
      if (m.includes("not opened") || m.includes("not open")) {
        const opensAtMs = ctx.startMs !== undefined ? ctx.startMs - EARLY_JOIN_MS : undefined;
        const when = opensAtMs !== undefined ? ` It opens ${fmtMoment(opensAtMs, now)}.` : " Try again a little before its start time.";
        return { kind: "notopen", text: `This meeting hasn't opened yet.${when} We'll keep trying and let you in as soon as it does.`, ...(opensAtMs !== undefined ? { opensAtMs } : {}) };
      }
      if (m.includes("full")) return { kind: "retry", text: "This meeting is full right now. Try again in a moment." };
      return { kind: "retry", text: message || "The meeting can't be joined right now. Try again." };
    case 410:
      return { kind: "fatal", text: "This meeting has ended or was cancelled." };
    case 423:
      return { kind: "retry", text: "The host has locked this meeting, so no one new can join. Ask the host to unlock it, then try again." };
    case 429:
      return { kind: "retry", text: "Too many attempts. Wait a few seconds and try again." };
    case 501:
      return { kind: "fatal", text: "Video meetings aren't set up on this server yet." };
    case 503:
      return { kind: "retry", text: "The meeting server is busy or can't be reached right now. Try again in a moment." };
    default:
      return { kind: "retry", text: message || "Something went wrong while joining. Try again." };
  }
}

/** A short message for any other failed action (cancel, invite, RSVP, ...). */
export function errText(e: unknown, what = "do that"): string {
  const { status, message } = asHttp(e);
  if (status === 0) return "Can't reach the server. Check your connection and try again.";
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return `You don't have permission to ${what}.`;
  if (status === 404) return "That wasn't found. It may have been removed, or you no longer have access.";
  if (status === 503) return "The service is busy right now. Try again in a moment.";
  if (message) return message;
  return `Couldn't ${what}. Try again.`;
}

export type MinutesAction = "load" | "generate" | "regenerate" | "send" | "tick";

/** Minutes errors, per the contract (section 9): 404 no transcript, 403 not allowed / AI off, 409 in progress. */
export function minutesErrorText(e: unknown, action: MinutesAction): string {
  const { status, message } = asHttp(e);
  const m = message.toLowerCase();
  if (status === 0) return "Can't reach the server. Check your connection and try again.";
  if (action === "generate" || action === "regenerate") {
    if (status === 404) return "There is no transcript for this meeting yet, so there is nothing to write minutes from. Minutes need captions or a recording: turn on captions during the meeting, or record it, and try again once the recording has been processed.";
    if (status === 403) return "You can't generate minutes for this meeting. Only the host can, and AI notes must be switched on for it (they are off for end-to-end encrypted meetings or when your organisation has disabled them).";
    if (status === 409) return m.includes("anonym") ? "The transcript was anonymised, so minutes can't be written from it." : "Minutes are already being generated. Wait a moment and refresh.";
    if (status === 501) return "AI minutes aren't set up on this server.";
  }
  if (action === "send") {
    if (status === 403) return "Only the host can send the minutes.";
    if (status === 409) return "The minutes are still being generated. Send them once they're ready.";
    if (status === 404) return "These minutes could not be found.";
  }
  if (action === "tick") {
    if (status === 403) return "You can't change this action item. Only the host or the person it is assigned to can.";
    if (status === 404) return "That action item could not be found.";
  }
  if (action === "load") {
    if (status === 403) return "You don't have access to these minutes.";
    if (status === 404) return "No minutes were found for this meeting.";
  }
  if (status === 401) return "Your session has ended. Sign in again.";
  return message || "Something went wrong. Try again.";
}
